<template>
  <StepLayout
    :on-save="onSave"
    :is-busy-on-save="isBusyOnSave"
    :on-next="onNext"
    :is-busy-on-next="isBusyOnNext"
    :on-back="onBack"
    :is-disabled="isDisabled"
    :uhoh="uhoh"
    :closeUhoh="closeUhoh"
    :uhohRoute="uhohRoute"
    help-link="https://signable.help/assigning-template-roles/#template-bulk"
    step-navigation-button-data-ec="bulk_send_role_page"
  >
    <template v-slot:title>
      {{ $t('bulkSend.steps.roles.title') }}
    </template>

    <RolesSelector
      :parties="parties"
      :templates="templates"
      @select="selectRole"
      @add="addParty"
      @remove="removeParty"
      @toggle="togglePartyRole"
    />
  </StepLayout>
</template>

<script>
// @ts-check
import {
  computed,
  defineComponent,
  onBeforeMount,
} from 'vue';
import { useRoute } from 'vue-router/composables';
import { useActions, useGetters } from 'vuex-composition-helpers/dist';

import {
  useBulkSendRoles,
  useBulkSendRolesParties,
} from '@/composables/bulkSend/roles';
import { useBulkSendStepsNavigation } from '@/composables/bulkSend/steps';
import useBulkSendUhoh from '@/composables/bulkSend/uhoh';

import StepLayout from '@/components/ui/bulkSend/StepLayout.vue';
import RolesSelector from '@/components/ui/bulkSend/RolesSelector.vue';

export default defineComponent({
  name: 'RolesPage',
  components: {
    StepLayout,
    RolesSelector,
  },
  setup() {
    const route = useRoute();
    /** @type {import('@/store/modules/bulkSend').Getters} */
    const { bulkSend } = useGetters({ bulkSend: 'bulkSend/bulkSend' });

    // templates
    const { fetchTemplates } = useActions({
      fetchTemplates: 'bulkSend/fetchTemplates',
    });
    onBeforeMount(() => fetchTemplates(route.params.fingerprint));

    const currentParties = computed(() => bulkSend.value.parties);
    const templates = computed(() => bulkSend.value.templates);
    const parties = useBulkSendRolesParties(currentParties, templates);

    // updates
    const updateValue = computed(() => parties.value);

    const isValid = computed(() => {
      const hasSigner = parties.value.some(party => party.role === 'signer');
      const roleIds = templates.value.flatMap(template =>
        template.parties.filter(party => party.role === 'signer').map(party => party.id));
      const assignedRoles = parties.value.flatMap(party => party.document_party_ids);

      return hasSigner && roleIds.every(id => assignedRoles.includes(id));
    });

    const {
      uhoh,
      closeUhoh,
      uhohRoute,
      onError,
    } = useBulkSendUhoh();

    const {
      onNext,
      isBusyOnNext,
      onSave,
      isBusyOnSave,
      onBack,
      isDisabled,
    } = useBulkSendStepsNavigation(
      'bulkSend/updateParties',
      updateValue,
      isValid,
      onError,
    );

    const {
      selectRole,
      deselectRole,
      addParty,
      removeParty,
      togglePartyRole,
    } = useBulkSendRoles(parties, templates);

    return {
      templates,
      parties,
      onNext,
      isBusyOnNext,
      onSave,
      isBusyOnSave,
      onBack,
      isDisabled,
      selectRole,
      deselectRole,
      addParty,
      removeParty,
      togglePartyRole,
      uhoh,
      closeUhoh,
      uhohRoute,
    };
  },
});
</script>
